import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { get, isEmpty } from 'lodash'
import { toast } from 'react-toastify'

import { fetchAttendants, attemptPinLogin, attemptPinLoginOffline, clearAttemptPinLoginStatus, clearFetchAttendantsStatus } from '../actions/attendant'
import { cleanupState } from '../actions/shared'
import { menusRequested } from '../actions/menus'

import { getAttemptPinLoginStatus, getFetchAttendantsStatus } from '../selectors/attendant'
import { getAttendantSettingsError } from '../selectors/attendantSettings'
import { getVenueName } from '../selectors/venue'

import useNumberPad from '../hooks/useNumberPad'
import useValidateEmployeePin from '../hooks/useValidateEmployeePin'

import UserLoginComponent from '../components/userLogin/Component'
import NavMenu from '../components/NavMenu'
import networkConnected from '../utils/networkConnected'
import { getEmployeeApprovalCodes } from '../selectors/employeeRole'
import { getCurrentMenu } from '../selectors/menus'

const UserLogin = ({ venueName, eventsLoading, pinLoginStatus, pinLoginStatusText, loadRevenueCenters, attemptPinLogin, attemptPinLoginOffline, onSuccess, onMount, onUnmount, approvalCodes, fetchAttendantsStatus }) => {
  const minLength = 4
  const maxLength = 5
  const [
    output,
    onInput,
    isValid,
  ] = useNumberPad(
    '',
    (value) => value,
    (value) => value.length >= minLength && value.length <= maxLength,
    maxLength,
    ''
  )
  
  const requestApproval = useValidateEmployeePin()
  const [showMenu, setShowMenu] = useState(false)

  const onMenuToggle = () => {
    setShowMenu(!showMenu)
  }

  const handleSubmit = () => {
    if (fetchAttendantsStatus === 'loading') {
      toast.error('Attendants are still being fetched. Please try again.')
    } else if (!isValid) {
      toast.error('Pin must be at least 4 characters')
    } else if (!approvalCodes.includes(output)) {
      toast.error('That code did not work')
    } else {
      networkConnected().then(() => {
        attemptPinLogin(output)
      })
      .catch(() => {
        attemptPinLoginOffline(output)
      })
    }
  }

  useEffect(() => {
    onMount()

    if (isEmpty(venueName)) {
      loadRevenueCenters()
    }

    return () => {
      onUnmount()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pinLoginStatus === 'succeeded') {
      onSuccess()
    }

    if (pinLoginStatus === 'failed') {
      onInput('', true)
      toast.error(pinLoginStatusText)
    }
  }, [pinLoginStatus])

  return (
    <>
      <UserLoginComponent
        venueName={venueName}
        eventsLoading={eventsLoading}
        pin={output}
        pinSize={maxLength}
        pinLoginStatus={pinLoginStatus}
        pinLoginStatusText={pinLoginStatusText}
        handlePinEntry={onInput}
        handleSubmit={handleSubmit}
        onOpenMenu={() => {
            requestApproval(output, () => {
              setShowMenu(true)
            })
          }}
        onSuccess={onSuccess}
        onInput={onInput}
      />
      {showMenu && <NavMenu onToggle={onMenuToggle} />}
    </>
  )
}

const mapStateToProps = (state, ownProps) => {
  const pinLoginStatus = getAttemptPinLoginStatus(state)
  const errorMessage = getAttendantSettingsError(state)
  const menu = getCurrentMenu(state)
  const approvalCodes = getEmployeeApprovalCodes(state)
  const fetchAttendantsStatus = getFetchAttendantsStatus(state)

  const PIN_LOGIN_STATUS_TEXT_MAP = {
    loading: 'Requesting Access',
    succeeded: 'Access Granted',
    failed: errorMessage,
  }

  const pinLoginStatusText = PIN_LOGIN_STATUS_TEXT_MAP[pinLoginStatus]

  return { venueName: menu.standName, pinLoginStatusText, pinLoginStatus, approvalCodes, fetchAttendantsStatus}
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const replace = get(ownProps, 'history.replace', () => {})
  const attendantsAlreadyFetched = ownProps?.history?.location?.state?.attendantsAlreadyFetched ?? false

  return {
    loadRevenueCenters: () => dispatch(menusRequested({ ignoreThrottle: true })),
    onMount: () => {
      dispatch(clearAttemptPinLoginStatus())
      dispatch(cleanupState())
      if (!attendantsAlreadyFetched) {
        dispatch(fetchAttendants())
      }
    },
    onUnmount: () => {
      dispatch(clearAttemptPinLoginStatus())
      dispatch(clearFetchAttendantsStatus())
    },
    attemptPinLogin: (pin) => {
      dispatch(attemptPinLogin(pin))
    },
    attemptPinLoginOffline: (pin) => {
      dispatch(attemptPinLoginOffline(pin))
    },
    onSuccess: () => {
      replace('/concession-order')
    },

  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserLogin)
